import type { ThemeConfig } from "./themeContract";

/**
 * The pre-refresh look, on the new shell.
 *
 * Values are taken from reference/current-ui.dc.html, which is the recreation
 * of the old UI: MUI defaults plus the navy/green brand, a larger type scale
 * (body 16, not 14), 5px radii and bold headings.
 *
 * `shell: "legacy"` mounts the recovered pre-refresh chrome — navy header with
 * greeting and Portal Management, plus the grey tab strip. That chrome is
 * quarantined in components/commonComponents/appShell/legacy/ and scheduled for
 * deletion; set this field to "topbar" or "rail" for the legacy palette on the
 * new shells instead.
 *
 * Faithful, not identical: the legacy pairings that failed contrast — the
 * bright-green login banner behind light-grey text (1.6:1), and the brand green
 * under white (2.3:1) — were hard-coded inside components, never tokens, so
 * reproducing the palette does not reproduce them. `.main` stays a fill and
 * `.text` carries words.
 */
const config: ThemeConfig = {
  id: "legacy",
  label: "Legacy — the current look on the new shell",
  shell: "legacy",
  fidelity: "mui",

  fonts: {
    // MUI's default stack, and deliberately NO googleHref: the original loaded
    // no webfont, so it rendered in whatever the machine already had — Roboto
    // is not installed on macOS, so it fell through to Helvetica. Pulling real
    // Roboto from Google is visibly different text.
    sans: "Roboto, Helvetica, Arial, sans-serif",
    mono: "ui-monospace, Menlo, monospace"
  },

  color: {
    railBg: "#004680",
    // The legacy TAB STRIP's grey — nothing else. An earlier comment here said
    // it was "reused for the you-have-left-a-client-context chrome so System
    // Management still looks like it used to", and that was wrong in both
    // directions: the original had no such chrome, and its System Management
    // header was the same navy as everywhere else. LegacyBar now ignores
    // `variant` for the header background.
    railSystemBg: "#4C4C4C",
    canvas: "#F5F5F5",
    surface: "#FFFFFF",
    border: "#E8E8E8",
    borderHover: "#BDBDBD",
    ink: "rgba(0,0,0,.87)",
    body: "#6B6B6B",
    muted: "#6B6B6B",
    // 5.3:1 on white — the old dash was #aaa at 1.9:1 and could not carry meaning.
    faint: "#6B6B6B",
    primary: "#004680",
    primaryHover: "#003A6B",
    positive: { main: "#38B54A", text: "#2E7D32", bg: "rgba(56,181,74,.14)" },
    caution: { main: "#F19E00", text: "#B25000", bg: "rgba(241,158,0,.16)" },
    negative: { main: "#E5484D", text: "#D32F2F", bg: "rgba(211,47,47,.12)" }
  },

  radius: { card: 5, control: 5, pill: 16 },
  shadow: {
    card: "0 2px 1px -1px rgba(0,0,0,.2),0 1px 1px 0 rgba(0,0,0,.14),0 1px 3px 0 rgba(0,0,0,.12)",
    cardHover: "0 3px 3px -2px rgba(0,0,0,.2),0 3px 4px 0 rgba(0,0,0,.14),0 1px 8px 0 rgba(0,0,0,.12)",
    popover: "0 5px 5px -3px rgba(0,0,0,.2),0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12)",
    modal: "0 11px 15px -7px rgba(0,0,0,.2),0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12)"
  },
  motion: { fast: "150ms", base: "200ms", slow: "250ms", easing: "cubic-bezier(.4,0,.2,1)" },
  layout: { railWidth: 240, contextBarHeight: 64, designWidth: 1400 },

  // Chart ink + card banner: the OLD app's literals, captured verbatim from
  // growthForecast.tsx during the 2026-08-06 tokenization pilot. Fidelity for
  // ported cards now lives here, not in the components.
  chart: {
    series: {
      activeAccount: "rgba(49, 144, 242, 1)",
      transaction: "rgba(0, 70, 128, 1)",
      revenue: "rgba(56, 181, 74, 1)",
      revenueTeal: "rgba(0, 189, 170, 1)",
      invoice: "rgba(90, 238, 158, 1)",
    },
    zeroLine: "red",
    delta: {
      up: "rgba(56, 181, 74, 1)",
      down: "rgba(250, 3, 3, 0.8)",
      upBg: "rgba(235, 253, 243, 1)",
      downBg: "rgba(254, 236, 238, 1)",
    },
    forecast: {
      actual: "rgba(49, 144, 242, 1)",
      line: "rgba(80, 205, 137, 1)",
      bounds: "rgba(240, 88, 34, 1)",
    },
    benchmark: { you: "rgba(80, 205, 137, 1)", average: "rgba(54, 153, 255, 1)" },
    risk: {
      stable: "rgba(56, 181, 74, 1)",
      risk1: "rgba(49, 144, 242, 1)",
      risk2: "rgba(250, 3, 3, 0.8)",
      stableBg: "rgba(90, 238, 158, 0.2)",
      risk1Bg: "rgba(49, 144, 242, 0.2)",
      risk2Bg: "rgba(250, 3, 3, 0.2)",
    },
    nps: {
      promoters: "rgba(0, 161, 77, 1)",
      passives: "rgba(0, 163, 225, 1)",
      detractors: "rgba(254, 0, 12, 1)",
    },
    gainLost: { gained: "rgba(59, 147, 74, 1)", lost: "rgba(240, 88, 34, 1)", active: "rgba(0, 163, 225, 1)" },
    goalLine: "rgba(250, 3, 3, 0.8)",
    gridline: "#f5f5f5",
    tooltipBg: "white",
  },
  cardChrome: {
    variant: "banner",
    headerBg: "#38B54A",
    headerText: "#FFFFFF",
    headerBorder: "1px solid grey",
    radius: 5,
    bodyBg: "#FFFFFF",
    bodyBorder: "1px solid grey",
    tileBg: "rgba(247, 249, 252, 1)",
    controlGroupBg: "rgba(231, 231, 231, 1)",
    controlActiveBg: "#FFFFFF",
    controlText: "black",
  },
  // Tile layer: the deferred literal classes of the 2026-08-06 batches,
  // captured verbatim from the five view files during the polish pass.
  // Fidelity values, not designed ones — statNegative plots 2.9:1 on the navy
  // rows and stays anyway, because that is what the old app rendered.
  tile: {
    eyebrow: "rgba(0, 0, 0, 1)",
    caption: "rgba(135, 135, 135, 1)",
    accentGreenBg: "rgba(68, 185, 85, 1)",
    accentGreenAltBg: "rgba(56, 181, 74, 1)",
    accentOrangeBg: "rgba(241, 158, 0, 1)",
    // Also stands in for the views' "#fff" spellings — identical computed colour.
    onAccent: "rgba(255, 255, 255, 1)",
    onAccentMuted: "rgba(218, 218, 218, 1)",
    statNegative: "rgba(255, 75, 85, 1)",
    chipBlueBg: "rgba(176, 215, 255, 0.3)",
    chipAmberBg: "rgba(255, 168, 0, 0.19)",
    chipOnAccentBg: "rgba(255, 255, 255, 0.2)",
    chipNegativeBg: "rgba(255, 75, 85, 0.2)",
    bodyTint: "rgba(181, 181, 195, 0.5)",
    piePanelBg: "rgba(0, 0, 0, 0.1)",
    stripBg: "rgba(243, 246, 249, 1)",
    panelTint: "rgba(176, 215, 255, 0.1)",
  },

  typography: {
    pageTitle: 34, screenTitle: 24, barTitle: 20, cardTitle: 16,
    sectionTitle: 20, labelTitle: 16,
    body: 16, caption: 12, eyebrow: 12, eyebrowTracking: ".03333em",
    buttonTextTransform: "uppercase",
    // 700, not 500: the old UI was bold everywhere. Carrying that in a token is
    // what lets the ~510 hard-coded `fontWeight: bold` declarations come out
    // without the app going light.
    headingWeight: 700
  }
};

export default config;
